import { useMemo, useState, type FunctionComponent } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined, RedoOutlined } from "@ant-design/icons";
import { Button, message, Popconfirm, Select, Space, Tree, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { productsSettingsActions } from "@fitness-app/app-store";
import { IntegrationClient, type INotionIntegration } from "@fitness-app/data-models/entities/ProductsSettings";

import GenericIntegrationForm from "~/components/GenericIntegrationForm/GenericIntegrationForm";
import ModalForm from "~/components/ModalForm/ModalForm";
import { useLoading } from "~/hooks/useLoading";
import { useAppDispatch } from "~/store/initializeStore";

interface OwnProps {
  integration: null | INotionIntegration[];
}

export interface NotionIntegrationModel {
  id?: string;
  name: string;
  apiSecret: string;
}

type Props = OwnProps;

const ADD_NEW_ITEM = "ADD_NEW_ITEM";

const NotionIntegration: FunctionComponent<Props> = ({ integration }) => {
  const { t } = useTranslation(["settings", "common", "dashboard"]);
  const [loading, startLoading, stopLoading] = useLoading();
  const [showModal, toggleModal] = useState(false);
  const dispatch = useAppDispatch();

  const [selectAccountId, changeSelectedAccount] = useState<undefined | string>(undefined);
  const [model, setModel] = useState<NotionIntegrationModel | undefined>(undefined);

  const selectedAccount = useMemo(() => {
    return integration?.find((account) => account.id === selectAccountId);
  }, [selectAccountId, integration]);

  const handleFormSubmit = async (data: NotionIntegrationModel) => {
    startLoading();
    const id = data.id || uuid();
    await dispatch(
      productsSettingsActions.integrateWithExternalApi({
        payload: { data: { ...data, id }, client: IntegrationClient.Notion },
        onSuccess: () => {
          message.success(t<string>(selectedAccount ? "dashboard:notion.editSuccess" : "dashboard:notion.success"));
          toggleModal(false);
          handleChange(id);
          setModel(undefined);
        },
        onFailure: (errorCode) => {
          if (errorCode === 403) {
            message.error(t<string>("dashboard:notion.accessError"));

            return;
          }
          message.error(t<string>("dashboard:notion.error"));
        },
      }),
    );
    stopLoading();
  };

  const handleChange = (value?: string | undefined) => {
    if (value === ADD_NEW_ITEM || !value) {
      changeSelectedAccount(undefined);
      toggleModal(true);

      return;
    }
    changeSelectedAccount(value);
  };

  const refreshConnectedLists = () => {
    if (!selectedAccount) {
      return;
    }
    message.loading(t<string>("dashboard:notion.refresh"));
    // await dispatch(
    //   productsSettingsActions.refreshIntegrateWithExternalApi({
    //     payload: { ...selectedAccount, client: IntegrationClient.Notion },
    //     onFailure: () => {
    //       message.destroy();
    //       message.error(t<string>('dashboard:notion.refreshError'));
    //     },
    //     onSuccess: () => {
    //       message.destroy();
    //       message.success(t<string>('dashboard:notion.refreshSuccess'));
    //     },
    //   })
    // );
  };

  const handleRemoveIntegration = async () => {
    if (!selectedAccount) {
      return;
    }
    message.loading(t<string>("messages.loading.removeAccount"));
    await dispatch(
      productsSettingsActions.removeIntegrationWithExternalApi({
        payload: { id: selectedAccount.id, client: IntegrationClient.Notion },
        onFailure: () => {
          message.destroy();
          message.error(t<string>("dashboard:notion.removeError"));
        },
        onSuccess: () => {
          message.destroy();
          message.success(t<string>("dashboard:notion.removeSuccess"));
        },
      }),
    );
  };

  return (
    <Space direction="vertical" size={24}>
      <Typography.Title level={4}>{t<string>("emailClients.activeCampaign.connectedAccounts")}</Typography.Title>
      <Select<string>
        size="large"
        placeholder={t<string>("selectAccount")}
        style={{ minWidth: 400 }}
        notFoundContent={false}
        onChange={handleChange}
        onSelect={handleChange}
        value={selectedAccount?.id}
        optionFilterProp="title"
        optionLabelProp="title"
      >
        {integration?.map((account) => (
          <Select.Option value={account.id} title={account.name} key={account.name}>
            {account.name}
          </Select.Option>
        ))}
        {
          <Select.Option value={ADD_NEW_ITEM} title={t<string>("addNewAccount")}>
            <Button
              type="link"
              style={{
                flex: "none",
                padding: "8px",
                display: "block",
                cursor: "pointer",
              }}
              className="space-x-2"
            >
              <PlusOutlined /> {t<string>("addNewAccount")}
            </Button>
          </Select.Option>
        }
      </Select>
      {selectedAccount && (
        <>
          <Space direction="horizontal" align="end" style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button type="dashed" icon={<RedoOutlined />} onClick={refreshConnectedLists}>
              {t<string>("common:button.refresh")}
            </Button>
            <Button
              type="dashed"
              icon={<EditOutlined />}
              onClick={() => {
                setModel({
                  id: selectedAccount.id,
                  name: selectedAccount.name,
                  apiSecret: "",
                });
                toggleModal(true);
              }}
            >
              {t<string>("common:button.edit")}
            </Button>
            <Popconfirm
              onConfirm={handleRemoveIntegration}
              title={t<string>("emailClients.deleteWarning")}
              okText={t<string>("common:button.delete")}
              okButtonProps={{ danger: true }}
            >
              <Button type="dashed" danger icon={<DeleteOutlined />}>
                {t<string>("common:button.delete")}
              </Button>
            </Popconfirm>
          </Space>
          <div>
            <Typography.Paragraph strong>{t<string>("dashboard:notion.databases")}</Typography.Paragraph>
            {selectedAccount.databases.length || selectedAccount.pages?.length ? (
              <Tree.DirectoryTree showIcon treeData={[]} />
            ) : (
              <Typography.Text type="secondary">{t<string>("emailClients.emptyLists")}</Typography.Text>
            )}
          </div>
        </>
      )}

      <ModalForm
        visible={showModal}
        loading={loading}
        onCancel={() => toggleModal(false)}
        title={selectedAccount ? t<string>("emailClients.editAccount") : t<string>("emailClients.addNewAccount")}
      >
        <GenericIntegrationForm<NotionIntegrationModel>
          fields={{
            id: {
              hidden: true,
              type: "text",
            },
            name: {
              label: t<string>("convertKit.account"),
              rules: [
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ],
              placeholder: t<string>("emailClients.accountNamePlaceholder"),
              autofocus: !selectedAccount,
              type: "text",
            },
            apiSecret: {
              label: t<string>("convertKit.apiSecret"),
              rules: [
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ],
              autofocus: !!selectedAccount,
              type: "text",
            },
          }}
          onSubmit={handleFormSubmit}
          model={model ? { name: model.name, id: model.id } : undefined}
        />
      </ModalForm>
    </Space>
  );
};

export default NotionIntegration;
